export const OAUTH_PARAM = {
  CODE: 'code',
  STATE: 'state',
  CALENDAR: 'calendar',
  INTEGRATION: 'integration',
  PROVIDER: 'provider',
} as const;

// Our polling flag, not a provider's: it is written after the callback params are gone
// and removed ~100s later, so it must never be in the strip set below.
export const OAUTH_POLL_PARAM = 'poll';

/**
 * Every param an OAuth redirect can land with: the grant and its signed envelope, the
 * discriminators our own redirect URIs carry, whatever each provider appends, and the
 * refusal shape. `state` is signed, not encrypted — it base64url-decodes to
 * {userId, provider, expiresAt}, so leaving it behind parks the Clerk id in the address
 * bar, the history entry and any outbound Referer.
 */
export const OAUTH_QUERY_PARAM_NAMES: ReadonlySet<string> = new Set<string>([
  OAUTH_PARAM.CODE,
  OAUTH_PARAM.STATE,
  OAUTH_PARAM.CALENDAR,
  OAUTH_PARAM.INTEGRATION,
  OAUTH_PARAM.PROVIDER,
  'scope_type',
  'scope',
  'authuser',
  'hd',
  'prompt',
  // Google's OpenID redirect appends `iss`, which none of the per-site cleanups ever
  // listed — it was the one param that survived on every route.
  'iss',
  'session_state',
  'client_info',
  'installation_id',
  'setup_action',
  'error',
  'error_description',
  'error_uri',
  'error_reason',
  // Meta documents error/error_reason/error_description, but its dialogs carry
  // error_code elsewhere.
  'error_code',
]);

// Google's redirect URI is /inbox and the exchange runs on /do-box, so the bounce
// between them carries the grant and the provider discriminator and nothing else.
//
// `error` rides along because RFC 6749 puts `state` on the refusal response too, and
// /do-box is in OAUTH_LANDING_ROUTES: without the error param the notifier there never
// fires, and the state — a signed envelope naming the user — parks in the address bar,
// the history entry and any outbound Referer with nothing left to strip it. Carrying
// both is what keeps the declined-consent path self-cleaning.
export const OAUTH_HANDOFF_PARAMS: ReadonlySet<string> = new Set<string>([
  OAUTH_PARAM.CODE,
  OAUTH_PARAM.STATE,
  OAUTH_PARAM.CALENDAR,
  OAUTH_PARAM.INTEGRATION,
  'error',
  'error_description',
]);

// Kept when the MCP callback forwards an expected refusal to /integration.
export const MCP_REFUSAL_FORWARD_PARAMS: ReadonlySet<string> = new Set<string>([
  'error',
  'error_description',
  OAUTH_PARAM.STATE,
]);

const NOTHING_KEPT: ReadonlySet<string> = new Set<string>();

export function stripOAuthQueryParams(
  params: URLSearchParams,
  keep: ReadonlySet<string> = NOTHING_KEPT,
): URLSearchParams {
  const remaining = new URLSearchParams(params);

  for (const name of OAUTH_QUERY_PARAM_NAMES) {
    if (!keep.has(name)) {
      remaining.delete(name);
    }
  }

  return remaining;
}

export function buildPathWithoutOAuthParams(
  pathname: string,
  params: URLSearchParams,
  keep?: ReadonlySet<string>,
): string {
  const remaining = stripOAuthQueryParams(params, keep).toString();

  return remaining ? `${pathname}?${remaining}` : pathname;
}
